import {getNodeId, chainWalk} from "./Util";
import {
    DEFAULT_FLEX_COLUMN_NUMBER_OF_SENSOR,
    ERROR_STRING,
    IN_PROGRESS_STRING,
    LIDAR_TESTER,
    LIDAR_TESTER_ACTIVATION_PERIOD_MINUTES,
    ONLINE_OK,
    SENSOR_INDEX_ONE,
    SENSOR_INDEX_ZERO,
    STATUS_COLORS,
    STATUS_MESSAGES,
    SUCCESS_STRING,
    TIMEOUT_STRING
} from "./Constants";
import {WW_FLEX_CART_RECIPES} from "./Validations";

// Helper function to get numberOfLidarSensor from flexible cart recipe
const getNumberOfFlexColumnSensor = (cartType, cartRecipesMap) => {
    const cartRecipe = cartType && cartRecipesMap && cartRecipesMap.get(cartType);
    return (cartRecipe && cartRecipe.numberOfLidarSensors) || DEFAULT_FLEX_COLUMN_NUMBER_OF_SENSOR; // Default to 3 if not found
};

// Helper function to process flexible cart columns in groups based on cart recipe
//For Example for the below config
// {
//     "columnAddress": "1.1",
//     "cartType": "WANZL_EIGHT_BIN_ZERO_OV_THREE_LIDAR",
//     "columnLabel": "A-1.A",
//     "offset": 15,
//     "offsetUnit": "CENTIMETERS"
// },
// {
//     "columnAddress": "1.2",
//     "cartType": "WANZL_EIGHT_BIN_ZERO_OV_THREE_LIDAR",
//     "columnLabel": "",
//     "offset": 15,
//     "offsetUnit": "CENTIMETERS"
// },
// {
//     "columnAddress": "1.3",
//     "cartType": "WANZL_EIGHT_BIN_ZERO_OV_THREE_LIDAR",
//     "columnLabel": "A-1.B",
//     "offset": 15,
//     "offsetUnit": "CENTIMETERS"
// },

// combineLabel is AB for above config

export const processFlexColumns = (flexColumns, cartRecipesMap) => {
    const results = [];
    if (flexColumns.length === 0) return results;
    
    flexColumns.sort((a, b) => parseFloat(a.columnAddress) - parseFloat(b.columnAddress));
    
    const flexColumnSensor =  getNumberOfFlexColumnSensor(flexColumns[0].cartType, cartRecipesMap);
    
    for (let i = 0; i < flexColumns.length; i += flexColumnSensor) {
        const group = flexColumns.slice(i, i + flexColumnSensor);
        const labels = [];
        
        group.forEach(column => {
            const label = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
            if (label) {
                labels.push(label);
            }
        });
        
        if (labels.length > 0) {
            const combinedLabel = [...new Set(labels.filter(l => l !== ''))].sort().join('');
            if (combinedLabel) {
                results.push({ combinedLabel, group });
            }
        }
    }
    return results;
};


/**
 * Creates a mapping between original column labels and unified column labels.
 * This function identifies related column labels (e.g., "YZ", "Y", "Z") and maps them to a unified label.
 * 
 * @param {Object} device - The lidar controller device object
 * @returns {Object} An object containing the label mapping and component mapping
 */
export const createColumnLabelMapping = (device) => {    
    const labelMapping = {};
    const componentMapping = {};
    const twoTierLabels = [];
    const oneTierLabels = [];
    
    // First pass: identify two-tier and one-tier column labels
    device.displayableAssetMappings.forEach(assetMapping => {
        if (!assetMapping.columns) return;
        
        assetMapping.columns.forEach(column => {            
            if (column.rowLabels && column.rowLabels.length > 1) {
                // Two-tier column (e.g., "YZ")
                const label = column.rowLabels.map(rowLabel => rowLabel[rowLabel.length - 1]).join('');
                twoTierLabels.push(label);
                
                // Initialize component mapping
                if (!componentMapping[label]) {
                    componentMapping[label] = column.rowLabels.map(rowLabel => rowLabel[rowLabel.length - 1]);
                }
            } else if (column.rowLabels && column.rowLabels.length === 1) {
                // One-tier OV column (e.g., "Y")
                const label = column.rowLabels[0][column.rowLabels[0].length - 1];
                oneTierLabels.push(label);
            } else {
                // Non-OV column (e.g., "A")
                const label = column.columnLabel[column.columnLabel.length - 1];
                oneTierLabels.push(label);
            }
        });
    });
    
    // Second pass: create mapping from original labels to unified labels
    // Map two-tier labels and their components first
    twoTierLabels.forEach(twoTierLabel => {
        // Map the two-tier label to itself
        labelMapping[twoTierLabel] = twoTierLabel;
        
        // Map each component of the two-tier label to the two-tier label
        const components = componentMapping[twoTierLabel];
        components.forEach(component => {
            labelMapping[component] = twoTierLabel;
        });
    });
    
    // Map remaining one-tier labels to themselves (only if not already mapped)
    oneTierLabels.forEach(label => {
        if (!labelMapping[label]) {
            labelMapping[label] = label;
        }
    });
    
    return { labelMapping, componentMapping };
};

/**
 * Generates a unified set of column labels from the device data.
 * Identifies related column labels (e.g., "Y", "Z", "YZ") and unifies them to single labels (e.g., "YZ").
 * Removes slash notation for cleaner UI display.
 * 
 * @param {Object} device - The lidar controller device object
 * @returns {Array} A sorted array of clean unified column labels
 */
export const getUnifiedColumnLabelSet = (device) => {
    const { labelMapping } = createColumnLabelMapping(device);    
    const unifiedLabels = new Set();
    
    // Collect all unique unified labels (no slash notation)
    Object.values(labelMapping).forEach(unifiedLabel => {
        unifiedLabels.add(unifiedLabel);
    });
    
    return Array.from(unifiedLabels).sort();
};

// The function takes in a column Label and rowLabel and returns the Label to be displayed on UI
// example for OV column containing T and U bins, we need to show "TU" on UI and so on

// The column here refers to the column in the racks at the delivery station, where the
// packages are stowed. for example column B in aisle A1: (it will have 4 bins: A1-1B, A1-2B, A1-3B, A1-4B)
export const getDisplayableColumnLabel = (columnLabel, rowLabels, cartType) => {
    // For flexible cart recipes, combine the column labels and ignore row labels
    // e.g., "A-1.A" and "A-1.B" should be combined to "AB" for flex rack
    if (rowLabels && !WW_FLEX_CART_RECIPES.includes(cartType)) {
        let label = "";
        rowLabels.forEach(rowLabel => {
            label = label + rowLabel[rowLabel.length - 1]
        })
        return label;
    }
    return columnLabel ? columnLabel[columnLabel.length - 1] : '';
}

/**
 * Analyzes device to determine if column groups should be unified or separate
 * based on flex consistency across all aisles
 */
const analyzeFlexConsistency = (device) => {
    const columnGroups = {};
    const aisleFlexGroups = {}; // Track flex groupings per aisle
    
    // Collect all columns and track flex groupings per aisle
    device.displayableAssetMappings.forEach(assetMapping => {
        const aisleLabel = assetMapping.aisleLabel;
        aisleFlexGroups[aisleLabel] = [];
        
        if (assetMapping.columns) {
            // Process flex columns to identify groupings in this aisle
            const flexColumns = assetMapping.columns.filter(col => 
                WW_FLEX_CART_RECIPES.includes(col.cartType)
            );
            
            if (flexColumns.length > 0) {
                const flexResults = processFlexColumns(flexColumns, new Map());
                flexResults.forEach(result => {
                    aisleFlexGroups[aisleLabel].push(result.combinedLabel);
                });
            }
            
            assetMapping.columns.forEach(column => {
                const label = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
                if (label) {
                    if (!columnGroups[label]) {
                        columnGroups[label] = [];
                    }
                    columnGroups[label].push({
                        ...column,
                        aisleLabel: assetMapping.aisleLabel,
                        isFlex: WW_FLEX_CART_RECIPES.includes(column.cartType)
                    });
                }
            });
        }
    });
    
    const consistencyMap = {};
    
    // Check each potential unified group considering flex grouping context
    Object.keys(columnGroups).forEach(label => {
        const columns = columnGroups[label];
        const allFlex = columns.every(col => col.isFlex);
        const allNonFlex = columns.every(col => !col.isFlex);
        
        if (allFlex) {
            // Check if this flex column has consistent grouping across aisles
            const flexGroupings = new Set();
            columns.forEach(col => {
                const aisleGroups = aisleFlexGroups[col.aisleLabel] || [];
                const groupContainingThisLabel = aisleGroups.find(group => group.includes(label));
                if (groupContainingThisLabel) {
                    flexGroupings.add(groupContainingThisLabel);
                }
            });
            
            // If flex column appears in different groupings, mark as mixed
            if (flexGroupings.size > 1) {
                consistencyMap[label] = 'mixed';
            } else {
                consistencyMap[label] = 'flex';
            }
        } else if (allNonFlex) {
            consistencyMap[label] = 'non-flex';
        } else {
            consistencyMap[label] = 'mixed';
        }
    });
    
    return { columnGroups, consistencyMap };
};

/**
 * Checks if an OV column was dynamically merged in a specific aisle
 * (as opposed to being pre-configured as combined)
 * 
 * @param {string} label - Column label to check (e.g., "TU", "WX")
 * @param {string} aisleLabel - Formatted aisle label (e.g., "C-18")
 * @param {Object} device - Device configuration
 * @returns {boolean} True if this aisle had individual components that got merged
 */
export const isAisleMergedOV = (label, aisleLabel, device) => {
    // Only check OV columns with multiple characters
    if (!isOVColumnLabel(label) || label.length <= 1) return false;
    
    // Find this specific aisle's configuration
    const aisle = device.displayableAssetMappings.find(
        mapping => formatAisleLabel(mapping.aisleLabel) === aisleLabel
    );
    
    if (!aisle || !aisle.columns) return false;
    
    // Get individual components (e.g., ["T", "U"] from "TU")
    const components = label.split('');
    
    // Check if this aisle originally had individual components
    const hasIndividualComponents = aisle.columns.some(column => {
        const columnLabel = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
        return components.includes(columnLabel) && 
               column.rowLabels && 
               column.rowLabels.length === 1; // Individual, not pre-combined
    });
    
    return hasIndividualComponents;
};

/**
 * Check if the aisle has any column with flexible rack or not
 * @param {Object} device 
 * @param {String} aisleLabel 
 * @param {List} columnLabels 
 * @returns {boolean} True if aisle has any column with flexible rack.
 */
export const hasFlexColumnsForAisle = (device, aisleLabel, columnLabels) => {
    const assetMapping = device.displayableAssetMappings.find(
        mapping => formatAisleLabel(mapping.aisleLabel) === aisleLabel
    );
    
    if (!assetMapping || !assetMapping.columns) return false;
    
    // Check if ALL specified column labels have flex cart types in this aisle
    return columnLabels.every(columnLabel => {
        return assetMapping.columns.some(column => {
            const label = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
            return label === columnLabel && WW_FLEX_CART_RECIPES.includes(column.cartType);
        });
    });
};

// The function takes in the lidar controller device and returns an exhaustive list of all the
// columns present in any of the aisles served by this controller.
// Uses priority-based processing: Check flex consistency first, then process accordingly
export const getExhaustiveLabelSet = (device, cartRecipesMap) => {
    const labelSet = new Set();
    const { consistencyMap } = analyzeFlexConsistency(device);
    
    // Separate flex and non-flex columns
    const allFlexColumns = [];
    const allNonFlexColumns = [];
    
    device.displayableAssetMappings.forEach(assetMapping => {
        if (assetMapping.columns) {
            assetMapping.columns.forEach(column => {
                if (WW_FLEX_CART_RECIPES.includes(column.cartType)) {
                    allFlexColumns.push(column);
                } else {
                    allNonFlexColumns.push(column);
                }
            });
        }
    });
    
    // FLEX COLUMN PROCESSING FOR MIXED CONFIGURATIONS:
    // Process flex columns - for mixed configurations add individual labels
    // Example: If "JK" is mixed (some aisles have J,K separate, others have JK unified),
    // add both "J" and "K" to labelSet for consistent display across all aisles
    const flexComponentLabels = new Set();
    if (allFlexColumns.length > 0) {
        const flexResults = processFlexColumns(allFlexColumns, cartRecipesMap);
        flexResults.forEach(({ combinedLabel, group }) => {
            // For mixed configurations (2-char combined labels), add individual labels
            if (combinedLabel.length === 2) {
                const leftLabel = combinedLabel[0];   // e.g., "J" from "JK"
                const rightLabel = combinedLabel[1];  // e.g., "K" from "JK"
                
                // Check if either component has mixed consistency across aisles
                if (consistencyMap[leftLabel] === "mixed" || consistencyMap[rightLabel] === "mixed") {
                    labelSet.add(leftLabel);
                    labelSet.add(rightLabel);
                    flexComponentLabels.add(leftLabel);
                    flexComponentLabels.add(rightLabel);
                } else {
                    // Consistent flex - use combined label
                    labelSet.add(combinedLabel);
                }
            } else {
                // Non-2-char combined label - use as is
                labelSet.add(combinedLabel);
            }
            
            group.forEach(column => {
                const label = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
                if (label && label.trim()) {
                    flexComponentLabels.add(label);
                }
            });
        });
    }
    
    // Process non-flex columns through unification, excluding flex components
    if (allNonFlexColumns.length > 0) {
        const deviceForUnification = { displayableAssetMappings: [{ columns: allNonFlexColumns }] };
        const unifiedLabels = getUnifiedColumnLabelSet(deviceForUnification);
        unifiedLabels.forEach(label => {
            // Only add if it's not already covered by a flex column
            if (!flexComponentLabels.has(label)) {
                labelSet.add(label);
            }
        });
    }
    
    return Array.from(labelSet).filter(label => label && label.trim()).sort();
}

// The function takes in the lidar controller device and return the list of
// column mappings for each aisle, sorted in ascending order i.e. A1 comes first, then A2 and so on
export const getSortedAssetByAisle = (device) => {
    return [...device.displayableAssetMappings.sort((a, b) => {
        return a.aisleLabel.localeCompare(b.aisleLabel, undefined, {
            numeric: true,
            sensitivity: 'base'
        });
    })]
}

// The function takes in the sensor state values for a particular column
// and returns an array, each entry representing a single Lidar sensor.
export const getColumnSensorStateValues = (columnSensorStates, isSensorLevelConfigEnabled) => {
    let values = []
    Object.keys(columnSensorStates).forEach((key) => {
        values.push({...columnSensorStates[key][SENSOR_INDEX_ZERO]})
        if (!isSensorLevelConfigEnabled) {
            values.push({...columnSensorStates[key][SENSOR_INDEX_ONE]})
        }
    })
    return values;
}

// The function removes the "-" from aisle Label to maintain uniformity across regions.
export const formatAisleLabel = (aisleLabel) => {
    // Add safety check to prevent errors with undefined or null values
    try {
        return aisleLabel.replace("-", "");
    } catch (error) {
        console.error("Error in formatAisleLabel:", error, "for aisleLabel:", aisleLabel);
        return ""; // Return a safe string
    }
}

// The function returns the mqtt topic to subscribe for listening to
// lidar events of a particular lidar controller
export const getMqttTopicForLidarTester = (nodeId, ip) => {
    return "site/" + nodeId + "/lidarTester/" + ip
}

// The function returns the Lidar Tester activation request object.
export const getLidarTesterActivationRequest = () => {
    return {
        nodeId: getNodeId(),
        commandType: LIDAR_TESTER,
        commandParams: JSON.stringify({
            event: LIDAR_TESTER,
            period: LIDAR_TESTER_ACTIVATION_PERIOD_MINUTES
        })
    }
}

// The function classifies a column label as OV or non-OV
export const isOVColumnLabel = (label) => {
    // To-Do: Explore a better way for this
    return label > "L";
}

/**
 * Configuration object for branch provisioning status display with styling and messages
 * Maps status strings to their corresponding UI properties (colors, messages)
 */
export const BRANCH_STATUS_CONFIG = {
    [SUCCESS_STRING]: {
        backgroundColor: STATUS_COLORS.SUCCESS.BACKGROUND,
        textColor: STATUS_COLORS.SUCCESS.TEXT,
        getMessage: (detectedCount) => STATUS_MESSAGES.BRANCH_SUCCESS.replace('{count}', detectedCount)
    },
    [ERROR_STRING]: {
        backgroundColor: STATUS_COLORS.ERROR.BACKGROUND,
        textColor: STATUS_COLORS.ERROR.TEXT,
        getMessage: (expectedCount, detectedCount) => 
            STATUS_MESSAGES.BRANCH_ERROR
                .replace('{expected}', expectedCount)
                .replace('{detected}', detectedCount)
    },
    [TIMEOUT_STRING]: {
        backgroundColor: STATUS_COLORS.WARNING.BACKGROUND,
        textColor: STATUS_COLORS.WARNING.TEXT,
        getMessage: () => STATUS_MESSAGES.BRANCH_TIMEOUT
    }
};

/**
 * Configuration object for leaf provisioning status display with styling and messages
 * Maps status strings to their corresponding UI properties (colors, messages)
 */
export const LEAF_STATUS_CONFIG = {
    [SUCCESS_STRING]: {
        backgroundColor: STATUS_COLORS.SUCCESS.BACKGROUND,
        textColor: STATUS_COLORS.SUCCESS.TEXT,
        getMessage: () => STATUS_MESSAGES.LEAF_SUCCESS
    },
    [ERROR_STRING]: {
        backgroundColor: STATUS_COLORS.ERROR.BACKGROUND,
        textColor: STATUS_COLORS.ERROR.TEXT,
        getMessage: () => STATUS_MESSAGES.LEAF_ERROR
    },
    [TIMEOUT_STRING]: {
        backgroundColor: STATUS_COLORS.WARNING.BACKGROUND,
        textColor: STATUS_COLORS.WARNING.TEXT,
        getMessage: () => STATUS_MESSAGES.LEAF_TIMEOUT
    },
    [IN_PROGRESS_STRING]: {
        backgroundColor: STATUS_COLORS.INFO.BACKGROUND,
        textColor: STATUS_COLORS.INFO.TEXT,
        getMessage: () => STATUS_MESSAGES.LEAF_IN_PROGRESS
    }
};

// Helper functions for device status badges
export const getConnectivityBadgeStyle = (connectivity) => {
    const isConnected = connectivity && connectivity.toLowerCase() === ONLINE_OK;
    return {
        display: 'inline-block',
        backgroundColor: isConnected ? '#00A643' : '#FF5963',
        color: '#ffffff',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        marginLeft: '10px'
    };
};

export const getStatusBadgeStyle = (status) => {
    const isActive = status && status.toUpperCase() === 'ACTIVE';
    return {
        display: 'inline-block',
        backgroundColor: isActive ? '#00A643' : '#007BFF',
        color: '#ffffff',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        marginLeft: '10px'
    };
};

/**
 * Determines if a column is a flex column
 * 
 * @param {string} label - Column label to check
 * @returns {boolean} True if column is flex type
 * 
 * @example
 * isFlexColumn("AB") // true (multi-char, non-OV)
 * isFlexColumn("TU") // false (multi-char, but OV)
 * isFlexColumn("A")  // false (single char)
 */
export const isFlexColumn = (label) => {
    return label && label.length > 1 && !isOVColumnLabel(label);
};

/**
 * Determines if a column is a unified OV column
 * 
 * @param {string} label - Column label to check
 * @returns {boolean} True if column is unified OV type
 * 
 * @example
 * isUnifiedOVColumn("TU") // true (multi-char OV)
 * isUnifiedOVColumn("HJ") // false (multi-char, but flex)
 * isUnifiedOVColumn("T")  // false (single char OV)
 */
export const isUnifiedOVColumn = (label) => {
    return label && label.length > 1 && isOVColumnLabel(label) && !isFlexColumn(label);
};

/**
 * Determines if a column is a regular (single character) column
 * 
 * @param {string} label - Column label to check
 * @returns {boolean} True if column is regular type
 * 
 * @example
 * isRegularColumn("A")  // true (single char)
 * isRegularColumn("AB") // false (multi-char)
 * isRegularColumn("")   // false (empty)
 */
export const isRegularColumn = (label) => {
    return label && label.length === 1;
};

/**
 * Determines if a column should use wide width (140px vs 70px)
 * 
 * @param {string} label - Column label to check
 * @returns {boolean} True if column should be wide
 * 
 * @example
 * shouldUseWideWidth("AB") // true (multi-char)
 * shouldUseWideWidth("TU") // true (OV column)
 * shouldUseWideWidth("A")  // false (single char)
 */
export const shouldUseWideWidth = (label) => {
    return (label && label.length > 1) || isOVColumnLabel(label);
};

/**
 * Calculates sensor states based on column type and merge state
 * Delegates to appropriate calculation function based on column characteristics
 * 
 * @param {Object} params.testerViewModel - The tester view model
 * @param {string} params.aisleLabel - Aisle label
 * @param {string} params.label - Column label
 * @param {boolean} params.isSensorLevelConfigEnabled - Sensor config flag
 * @param {boolean} params.shouldMergeRight - Whether merging right
 * @param {boolean} params.shouldMergeLeft - Whether merging left
 * @returns {Object} { sensorStates, isAnomaly }
 * 
 * @example
 * calculateSensorStatesByType({testerViewModel: viewModel, aisleLabel: "A-1", label: "TU", isSensorLevelConfigEnabled: true, 
 *                              shouldMergeRight: false, shouldMergeLeft: false})
 * Returns: { sensorStates: [...], isAnomaly: false }
 */
export const calculateSensorStatesByType = ({ testerViewModel, aisleLabel, label, isSensorLevelConfigEnabled, shouldMergeRight, shouldMergeLeft }) => {
    // Handle unified OV columns (e.g., "TU")
    if (isUnifiedOVColumn(label)) {
        return calculateMergedOVSensorStates({
            testerViewModel,
            aisleLabel,
            label,
            isSensorLevelConfigEnabled
        });
    }
    
    // Handle flex columns with visual merging
    if (shouldMergeRight) {
        return calculateFlexSensorStates({
            testerViewModel,
            aisleLabel,
            label,
            isSensorLevelConfigEnabled,
            isLeftColumn: true // Left side of merge shows 2 sensors
        });
    }
    
    if (shouldMergeLeft) {
        return calculateFlexSensorStates({
            testerViewModel,
            aisleLabel,
            label,
            isSensorLevelConfigEnabled,
            isLeftColumn: false // Right side of merge shows 1 sensor
        });
    }
    
    // Handle flex columns without merging (e.g., "HJ" when not adjacent to other flex)
    if (isFlexColumn(label)) {
        // Check if combined data exists, otherwise look for component data
        const combinedData = chainWalk(() => 
            testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][label], null);
        
        if (combinedData) {
            const sensorStates = getColumnSensorStateValues(combinedData, isSensorLevelConfigEnabled);
            return { sensorStates, isAnomaly: false };
        }
        
        // For flex columns, construct 3 sensors: left from first component, middle shared, right from last component
        const componentColumns = label.split('');
        const sensorStates = [];
        let hasAnyComponentData = false;
        
        // Get left sensor from first component (H)
        const firstComponentData = chainWalk(() => 
            testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][componentColumns[0]], null);
        if (firstComponentData) {
            hasAnyComponentData = true;
            const states = getColumnSensorStateValues(firstComponentData, isSensorLevelConfigEnabled);
            if (states && states[0]) sensorStates.push(states[0]); // Left sensor
            if (states && states[1]) sensorStates.push(states[1]); // Middle sensor (shared)
        }
        
        // Get right sensor from last component (J) - only if different from first
        if (componentColumns.length > 1) {
            const lastComponentData = chainWalk(() => 
                testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][componentColumns[componentColumns.length - 1]], null);
            if (lastComponentData) {
                hasAnyComponentData = true;
                const states = getColumnSensorStateValues(lastComponentData, isSensorLevelConfigEnabled);
                if (states && states[0]) sensorStates.push(states[0]); // Right sensor
            }
        }
        
        return {
            sensorStates: sensorStates.length > 0 ? sensorStates : null,
            isAnomaly: !hasAnyComponentData
        };
    }
    
    // Handle regular single columns
    return calculateRegularSensorStates({
        testerViewModel,
        aisleLabel,
        label,
        isSensorLevelConfigEnabled
    });
};

/**
 * Calculates sensor states for merged OV columns
 * Handles both merged column data and component column fallback
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.testerViewModel - The tester view model
 * @param {string} params.aisleLabel - Aisle label
 * @param {string} params.label - Column label
 * @param {boolean} params.isSensorLevelConfigEnabled - Sensor config flag
 * @returns {Object} { sensorStates, isAnomaly }
 * 
 * @example
 * calculateMergedOVSensorStates({ testerViewModel: viewModel, aisleLabel: "A-1",  label: "TU", isSensorLevelConfigEnabled: true})
 * Returns: { sensorStates: [...], isAnomaly: false }
 */
export const calculateMergedOVSensorStates = ({ testerViewModel, aisleLabel, label, isSensorLevelConfigEnabled }) => {
    // Check if merged column exists
    const mergedColumnExists = chainWalk(() => 
        testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][label], false);
    
    if (mergedColumnExists) {
        // Use merged column data
        const sensorStates = chainWalk(() => 
            getColumnSensorStateValues(testerViewModel.sensorStateMap[aisleLabel][label], isSensorLevelConfigEnabled), null);
        return { sensorStates, isAnomaly: false };
    }
    
    // Fallback to component columns
    const componentColumns = label.split('');
    const componentStates = [];
    let hasAnyComponentData = false;
    
    componentColumns.forEach(componentCol => {
        const componentData = chainWalk(() => 
            testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][componentCol], null);
        if (componentData) {
            hasAnyComponentData = true;
            const states = getColumnSensorStateValues(componentData, isSensorLevelConfigEnabled);
            if (states) componentStates.push(...states);
        }
    });
    
    return {
        sensorStates: componentStates.length > 0 ? componentStates : null,
        isAnomaly: !hasAnyComponentData
    };
};

/**
 * Calculates sensor states for flex columns with visual merging
 * Handles left/right sensor distribution for merged display
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.testerViewModel - The tester view model
 * @param {string} params.aisleLabel - Aisle label
 * @param {string} params.label - Column label
 * @param {boolean} params.isSensorLevelConfigEnabled - Sensor config flag
 * @param {boolean} params.isLeftColumn - True for left side of merge
 * @returns {Object} { sensorStates, isAnomaly }
 * 
 * @example
 * calculateFlexSensorStates({ testerViewModel: viewModel, aisleLabel: "A-1", label: "J",  isSensorLevelConfigEnabled: true, isLeftColumn: true})
 * Returns: { sensorStates: [sensor0, sensor1], isAnomaly: false }
 */
export const calculateFlexSensorStates = ({ testerViewModel, aisleLabel, label, isSensorLevelConfigEnabled, isLeftColumn }) => {
    const allSensors = chainWalk(() => 
        getColumnSensorStateValues(testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel] && testerViewModel.sensorStateMap[aisleLabel][label], isSensorLevelConfigEnabled), null) || [];
    
    const isAnomaly = chainWalk(() => 
        !testerViewModel || !testerViewModel.sensorStateMap || !testerViewModel.sensorStateMap[aisleLabel] || !testerViewModel.sensorStateMap[aisleLabel][label], null);
    
    // Left column shows first 2 sensors (left + middle)
    // Right column shows last 1 sensor (right)
    const sensorStates = isLeftColumn ? allSensors.slice(0, 2) : allSensors.slice(-1);
    
    return { sensorStates, isAnomaly };
};

/**
 * Calculates regular sensor states for single columns
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.testerViewModel - The tester view model
 * @param {string} params.aisleLabel - Aisle label
 * @param {string} params.label - Column label
 * @param {boolean} params.isSensorLevelConfigEnabled - Sensor config flag
 * @returns {Object} { sensorStates, isAnomaly }
 */
export const calculateRegularSensorStates = ({ testerViewModel, aisleLabel, label, isSensorLevelConfigEnabled }) => {
    const sensorStates = chainWalk(() => 
        getColumnSensorStateValues(testerViewModel && testerViewModel.sensorStateMap && testerViewModel.sensorStateMap[aisleLabel]
            && testerViewModel.sensorStateMap[aisleLabel][label], isSensorLevelConfigEnabled), null);
    
    const isAnomaly = chainWalk(() => 
        !testerViewModel || !testerViewModel.sensorStateMap || !testerViewModel.sensorStateMap[aisleLabel] || !testerViewModel.sensorStateMap[aisleLabel][label], null);
    
    return { sensorStates, isAnomaly };
};

/**
 * Determines merge direction for a column based on adjacent columns
 * 
 * @param {Array} params.exhaustiveLabelSet - Complete set of column labels
 * @param {number} params.binIndex - Current column index
 * @param {Object} params.device - Device configuration
 * @param {string} params.aisleLabel - Aisle label
 * @returns {Object} { shouldMergeRight, shouldMergeLeft }
 * 
 * @example
 * determineMergeDirection({exhaustiveLabelSet: ["J", "K", "L"], binIndex: 0, device: deviceConfig, aisleLabel: "A-1"})
 * Returns: { shouldMergeRight: true, shouldMergeLeft: false }
 */
export const determineMergeDirection = ({ exhaustiveLabelSet, binIndex, device, aisleLabel }) => {
    let shouldMergeRight = false;
    let shouldMergeLeft = false;

    if (exhaustiveLabelSet) {
        if (binIndex < exhaustiveLabelSet.length - 1) {
            const currentLabel = exhaustiveLabelSet[binIndex];
            const nextLabel = exhaustiveLabelSet[binIndex + 1];
            shouldMergeRight = hasFlexColumnsForAisle(device, aisleLabel, [currentLabel, nextLabel]);
        }
    
        if (binIndex > 0) {
            const currentLabel = exhaustiveLabelSet[binIndex];
            const prevLabel = exhaustiveLabelSet[binIndex - 1];
            shouldMergeLeft = hasFlexColumnsForAisle(device, aisleLabel, [prevLabel, currentLabel]);
        }
    }
    
    return { shouldMergeRight, shouldMergeLeft };
};



// WEBPACK FOOTER //
// ./src/components/util/LidarTesterUtil.js